import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { securityAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Shield,
  Lock,
  Key,
  Wifi,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  Trash2,
} from 'lucide-react';

export const SecurityPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newIP, setNewIP] = useState('');

  const isHROrAdmin = user?.role_name?.toLowerCase().includes('admin') || user?.role_name?.toLowerCase() === 'hr';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, logsRes] = await Promise.all([
        securityAPI.getSettings().catch(() => ({ data: null })),
        securityAPI.getDeviceLogs().catch(() => ({ data: [] })),
      ]);
      setSettings(settingsRes.data || {
        ip_restriction_enabled: false,
        allowed_ips: [],
        max_login_attempts: 5,
        lockout_duration_minutes: 30,
        session_timeout_minutes: 480,
        require_2fa: false,
        password_expiry_days: 90,
        min_password_length: 8,
      });
      setDeviceLogs(logsRes.data);
    } catch (err) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await securityAPI.updateSettings(settings);
      toast.success('Security settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const addIP = () => {
    if (!newIP) return;
    // Basic IP validation
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(newIP)) {
      toast.error('Invalid IP address format');
      return;
    }
    setSettings({
      ...settings,
      allowed_ips: [...(settings.allowed_ips || []), newIP],
    });
    setNewIP('');
  };

  const removeIP = (ip) => {
    setSettings({
      ...settings,
      allowed_ips: settings.allowed_ips.filter(i => i !== ip),
    });
  };

  const deviceIcons = {
    desktop: <Monitor className="h-4 w-4" />,
    mobile: <Smartphone className="h-4 w-4" />,
    tablet: <Tablet className="h-4 w-4" />,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!isHROrAdmin) {
    // Show only device logs for regular users
    return (
      <div className="space-y-6" data-testid="security-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Security
          </h1>
          <p className="mt-1 text-slate-600">View your login activity</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>My Login Activity</CardTitle>
            <CardDescription>Recent device logins to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deviceLogs.slice(0, 20).map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {deviceIcons[log.device_type] || <Globe className="h-4 w-4" />}
                        <span className="capitalize">{log.device_type}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{log.ip_address}</TableCell>
                    <TableCell className="capitalize">{log.action.replace('_', ' ')}</TableCell>
                    <TableCell>{log.timestamp?.split('T')[0]} {log.timestamp?.split('T')[1]?.slice(0, 8)}</TableCell>
                    <TableCell>
                      {log.success ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="h-3 w-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {deviceLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No login activity found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Security Settings
          </h1>
          <p className="mt-1 text-slate-600">Configure security policies for your hospital</p>
        </div>
        <Button 
          onClick={handleSave} 
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={saving}
          data-testid="save-security-btn"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="access" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="access">
            <Lock className="h-4 w-4 mr-2" />
            Access Control
          </TabsTrigger>
          <TabsTrigger value="password">
            <Key className="h-4 w-4 mr-2" />
            Password Policy
          </TabsTrigger>
          <TabsTrigger value="ip">
            <Wifi className="h-4 w-4 mr-2" />
            IP Restrictions
          </TabsTrigger>
          <TabsTrigger value="logs">
            <Monitor className="h-4 w-4 mr-2" />
            Device Logs
          </TabsTrigger>
        </TabsList>

        {/* Access Control Tab */}
        <TabsContent value="access">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Login Security</CardTitle>
                <CardDescription>Configure login attempt limits and lockout policies</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="max_attempts">Max Login Attempts</Label>
                  <Input
                    id="max_attempts"
                    type="number"
                    value={settings?.max_login_attempts || 5}
                    onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                    min="3"
                    max="10"
                    data-testid="max-attempts-input"
                  />
                  <p className="text-xs text-slate-500">Number of failed attempts before account lockout</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lockout_duration">Lockout Duration (minutes)</Label>
                  <Input
                    id="lockout_duration"
                    type="number"
                    value={settings?.lockout_duration_minutes || 30}
                    onChange={(e) => setSettings({ ...settings, lockout_duration_minutes: parseInt(e.target.value) })}
                    min="5"
                    max="1440"
                    data-testid="lockout-duration-input"
                  />
                  <p className="text-xs text-slate-500">How long accounts are locked after max failed attempts</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="session_timeout">Session Timeout (minutes)</Label>
                  <Input
                    id="session_timeout"
                    type="number"
                    value={settings?.session_timeout_minutes || 480}
                    onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
                    min="15"
                    max="1440"
                    data-testid="session-timeout-input"
                  />
                  <p className="text-xs text-slate-500">Auto logout after inactivity</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>Enhanced security with 2FA</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Shield className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">Require 2FA</p>
                      <p className="text-sm text-slate-500">Enforce 2FA for all users</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.require_2fa || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, require_2fa: checked })}
                    data-testid="require-2fa-switch"
                  />
                </div>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Coming Soon</p>
                      <p className="text-sm text-amber-700">2FA will be available in a future update</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Password Policy Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Password Policy</CardTitle>
              <CardDescription>Set password requirements for all users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="min_password">Minimum Password Length</Label>
                  <Input
                    id="min_password"
                    type="number"
                    value={settings?.min_password_length || 8}
                    onChange={(e) => setSettings({ ...settings, min_password_length: parseInt(e.target.value) })}
                    min="6"
                    max="20"
                    data-testid="min-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_expiry">Password Expiry (days)</Label>
                  <Input
                    id="password_expiry"
                    type="number"
                    value={settings?.password_expiry_days || 90}
                    onChange={(e) => setSettings({ ...settings, password_expiry_days: parseInt(e.target.value) })}
                    min="30"
                    max="365"
                    data-testid="password-expiry-input"
                  />
                  <p className="text-xs text-slate-500">Users will be required to change password after this period</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* IP Restrictions Tab */}
        <TabsContent value="ip">
          <Card>
            <CardHeader>
              <CardTitle>IP Restrictions</CardTitle>
              <CardDescription>Limit access to specific IP addresses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <Wifi className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">Enable IP Restriction</p>
                    <p className="text-sm text-slate-500">Only allow access from whitelisted IPs</p>
                  </div>
                </div>
                <Switch
                  checked={settings?.ip_restriction_enabled || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, ip_restriction_enabled: checked })}
                  data-testid="ip-restriction-switch"
                />
              </div>

              {settings?.ip_restriction_enabled && (
                <>
                  <Separator />
                  
                  <div className="space-y-4">
                    <Label>Allowed IP Addresses</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newIP}
                        onChange={(e) => setNewIP(e.target.value)}
                        placeholder="e.g., 192.168.1.0/24"
                        data-testid="new-ip-input"
                      />
                      <Button onClick={addIP} data-testid="add-ip-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add IP
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {(settings?.allowed_ips || []).map((ip, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span className="font-mono">{ip}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeIP(ip)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      {(settings?.allowed_ips || []).length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">
                          No IP addresses added. Add IPs to restrict access.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Device Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Device Login Logs</CardTitle>
              <CardDescription>Track all login attempts across devices</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deviceLogs.slice(0, 50).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.user_id?.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {deviceIcons[log.device_type] || <Globe className="h-4 w-4" />}
                          <span className="capitalize">{log.device_type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{log.ip_address}</TableCell>
                      <TableCell className="capitalize">{log.action.replace('_', ' ')}</TableCell>
                      <TableCell>{log.timestamp?.split('T')[0]} {log.timestamp?.split('T')[1]?.slice(0, 8)}</TableCell>
                      <TableCell>
                        {log.success ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Success
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <XCircle className="h-3 w-3 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {deviceLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No device logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityPage;
